/**
 * Device Service - Frontend
 * @description Service for device-related API operations
 */

import { api } from '../config';
import {
  ApiResponse,
  Device,
  DeviceCreateRequest,
  DeviceUpdateRequest,
  DeviceFormData,
  DeviceQueryParams,
} from '@/types';
import { deviceMappers } from '@/types/mappers';
// import { cleanUndefinedValues } from '../utils/apiUtils'; // stripMongoFields

export const deviceService = {
  // Get all devices with optional pagination and field selection
  async getAll(
    params?: DeviceQueryParams & { paths?: string[]; selection?: 'association' | 'basic' | 'full' }
  ): Promise<ApiResponse<Device[]>> {
    const response = await api.get<ApiResponse<Device[]>>('/devices', { params });
    return response.data;
  },

  // Get devices for associations (optimized for minimal data)
  async getAllForAssociations(): Promise<ApiResponse<Device[]>> {
    const response = await api.get<ApiResponse<Device[]>>('/devices', {
      params: { selection: 'association' },
    });
    return response.data;
  },

  // Get device by ID
  async getById(id: string): Promise<ApiResponse<Device>> {
    const response = await api.get<ApiResponse<Device>>(`/devices/${id}`);
    return response.data;
  },

  // Get device by serial number
  async getBySerialNumber(serialNumber: string): Promise<ApiResponse<Device>> {
    const response = await api.get<ApiResponse<Device>>(`/devices/serial/${serialNumber}`);
    return response.data;
  },

  // Create a new device
  async create(formData: DeviceFormData): Promise<ApiResponse<Device>> {
    const deviceData: DeviceCreateRequest = deviceMappers.toCreateRequest(formData);
    const response = await api.post<ApiResponse<Device>>('/devices', deviceData);
    return response.data;
  },

  // Update device
  async update(id: string, formData: DeviceFormData): Promise<ApiResponse<Device>> {
    const updateRequest: DeviceUpdateRequest = deviceMappers.toUpdateRequest(formData);
    const response = await api.put<ApiResponse<Device>>(`/devices/${id}`, updateRequest);
    return response.data;
  },

  // Delete device (permanent)
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/devices/${id}`);
    return response.data;
  },

  // Deactivate device (soft delete)
  async deactivate(id: string): Promise<ApiResponse<void>> {
    const response = await api.patch<ApiResponse<void>>(`/devices/${id}/deactivate`);
    return response.data;
  },

  // Reactivate device
  async reactivate(id: string): Promise<ApiResponse<void>> {
    const response = await api.patch<ApiResponse<void>>(`/devices/${id}/reactivate`);
    return response.data;
  },

  /** Reinicia la próxima secuencia esperada del dispositivo (admin). */
  async patchNextExpectedSequence(
    id: string,
    nextExpectedSequence: number
  ): Promise<ApiResponse<Device>> {
    const response = await api.patch<ApiResponse<Device>>(`/devices/${id}/next-expected-sequence`, {
      nextExpectedSequence,
    });
    return response.data;
  },

  // Get devices by user ID
  async getByUserId(userId: string): Promise<ApiResponse<Device[]>> {
    const response = await api.get<ApiResponse<Device[]>>(`/devices/user/${userId}`);
    return response.data;
  },

  // Get active devices
  async getActiveDevices(): Promise<ApiResponse<Device[]>> {
    const response = await api.get<ApiResponse<Device[]>>('/devices/status/active');
    return response.data;
  },
};
